const JOTFORM_DOMAIN_SRC_RE = /^https:\/\/form\.jotform\.com\/jsform\/(\d+)\/?$/i;
const JOTFORM_SRC_FRAGMENT_RE = /form\.jotform\.com\/jsform\/(\d+)/i;
const JOTFORM_FORM_URL_RE = /(?:https?:\/\/)?(?:[\w.-]+\.)?jotform\.com\/(?:form\/)?(\d+)/i;
const NUMERIC_ID_RE = /^(\d+)$/;

/**
 * Parse a Jotform form ID from a script tag, embed URL, or bare numeric ID.
 * Returns null when input is invalid or the URL is not on form.jotform.com.
 */
export function parseJotformFormId(input) {
  const trimmed = (input || '').trim();
  if (!trimmed) return null;

  const scriptMatch = trimmed.match(/<script[\s\S]*?src=["']([^"']+)["'][\s\S]*?>/i);
  if (scriptMatch) {
    const src = scriptMatch[1].trim();
    const domainMatch = src.match(JOTFORM_DOMAIN_SRC_RE);
    if (domainMatch) return domainMatch[1];
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    const urlMatch = trimmed.match(JOTFORM_SRC_FRAGMENT_RE);
    if (urlMatch && JOTFORM_DOMAIN_SRC_RE.test(trimmed.replace(/\s/g, ''))) {
      return urlMatch[1];
    }
    const formUrlMatch = trimmed.match(JOTFORM_FORM_URL_RE);
    if (formUrlMatch && NUMERIC_ID_RE.test(formUrlMatch[1])) {
      return formUrlMatch[1];
    }
    return null;
  }

  const fragmentMatch = trimmed.match(JOTFORM_SRC_FRAGMENT_RE);
  if (fragmentMatch && NUMERIC_ID_RE.test(fragmentMatch[1])) {
    return fragmentMatch[1];
  }

  const formUrlMatch = trimmed.match(JOTFORM_FORM_URL_RE);
  if (formUrlMatch && NUMERIC_ID_RE.test(formUrlMatch[1])) {
    return formUrlMatch[1];
  }

  const numericMatch = trimmed.match(NUMERIC_ID_RE);
  if (numericMatch) return numericMatch[1];

  return null;
}

export function isValidJotformFormId(formId) {
  return typeof formId === 'string' && NUMERIC_ID_RE.test(formId);
}

export function validateJotformInput(input) {
  const formId = parseJotformFormId(input);
  if (!formId) {
    return {
      valid: false,
      error: 'Enter a valid Jotform form ID or paste a script tag from form.jotform.com.',
    };
  }
  return { valid: true, formId };
}

function extractFormIdFromElement(el) {
  if (!el) return null;

  const iframe = el.querySelector?.('iframe[src*="jotform.com"]') || (el.matches?.('iframe[src*="jotform.com"]') ? el : null);
  if (iframe) {
    const match = iframe.getAttribute('src')?.match(JOTFORM_FORM_URL_RE);
    if (match && isValidJotformFormId(match[1])) return match[1];
  }

  const form = el.matches?.('form') ? el : el.querySelector?.('form');
  if (form) {
    if (isValidJotformFormId(form.id)) return form.id;
    const actionMatch = form.getAttribute('action')?.match(JOTFORM_FORM_URL_RE);
    if (actionMatch && isValidJotformFormId(actionMatch[1])) return actionMatch[1];
  }

  const html = el.innerHTML || el.outerHTML || '';
  const iframeMatch = html.match(/iframe[^>]+src=["'][^"']*jotform\.com[^"']*(\d+)/i);
  if (iframeMatch && isValidJotformFormId(iframeMatch[1])) return iframeMatch[1];

  const handlerMatch = html.match(/JotFormIFrame-(\d+)/i);
  if (handlerMatch && isValidJotformFormId(handlerMatch[1])) return handlerMatch[1];

  const jsformMatch = html.match(JOTFORM_SRC_FRAGMENT_RE);
  if (jsformMatch && isValidJotformFormId(jsformMatch[1])) return jsformMatch[1];

  return null;
}

function removeLegacyJotformFieldMarkup(doc, formId) {
  const fields = doc.querySelectorAll(
    'input[name^="q"], input[id^="input_"], select[name^="q"], textarea[name^="q"], button[type="submit"]',
  );
  if (fields.length === 0) return false;

  const blocks = new Set();
  fields.forEach((field) => {
    let node = field;
    while (node.parentElement && node.parentElement !== doc.body) {
      const parent = node.parentElement;
      if (parent.classList?.contains('jotform-embed')) return;
      if (['UL', 'OL', 'LI', 'DIV', 'FORM', 'P', 'SECTION'].includes(parent.tagName)) {
        node = parent;
      } else {
        break;
      }
    }
    if (!node.classList?.contains('jotform-embed')) {
      blocks.add(node);
    }
  });

  if (blocks.size === 0) return false;

  let placeholderInserted = false;
  blocks.forEach((block) => {
    if (formId && !placeholderInserted && !doc.querySelector('.jotform-embed')) {
      block.replaceWith(createPlaceholder(doc, formId));
      placeholderInserted = true;
    } else {
      block.remove();
    }
  });

  return true;
}

function createPlaceholder(doc, formId) {
  const embed = doc.createElement('div');
  embed.className = 'jotform-embed';
  embed.setAttribute('data-form-id', formId);
  return embed;
}

function isJotformArtifact(el) {
  if (!el || el.nodeType !== 1) return false;
  if (el.classList?.contains('jotform-embed')) return false;

  const tag = el.tagName.toLowerCase();
  if (tag === 'iframe' && el.getAttribute('src')?.includes('jotform.com')) return true;
  if (tag === 'form' && (
    el.classList.contains('jotform-form') ||
    isValidJotformFormId(el.id) ||
    el.getAttribute('action')?.includes('jotform.com')
  )) return true;
  if (el.classList?.contains('form-section') || el.classList?.contains('form-line')) return true;
  if (el.classList?.contains('jotform-wrapper')) return true;
  if (el.querySelector?.('form.jotform-form, .form-section, iframe[src*="jotform.com"]')) return true;

  return false;
}

function findJotformRoot(el, docBody) {
  let root = el;
  while (root.parentElement && root.parentElement !== docBody) {
    const parent = root.parentElement;
    if (parent.classList?.contains('jotform-embed')) return null;
    if (isJotformArtifact(parent) && !parent.classList?.contains('prose')) {
      root = parent;
    } else {
      break;
    }
  }
  return root;
}

/**
 * Strip executable HTML, raw Jotform markup, and preserve only embed placeholders.
 */
export function sanitizePostHtml(html) {
  if (!html || typeof html !== 'string') return html || '';
  if (typeof DOMParser === 'undefined') return html;

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const discoveredIds = [];

    const globalIdMatch =
      html.match(/form\.jotform\.com\/(?:form\/)?(\d+)/i) ||
      html.match(/JotFormIFrame-(\d+)/i) ||
      html.match(/jsform\/(\d+)/i);
    if (globalIdMatch && isValidJotformFormId(globalIdMatch[1])) {
      discoveredIds.push(globalIdMatch[1]);
    }

    doc.querySelectorAll('script').forEach((script) => {
      const src = (script.getAttribute('src') || '').trim();
      const match = src.match(JOTFORM_DOMAIN_SRC_RE);
      if (match) {
        discoveredIds.push(match[1]);
        script.replaceWith(createPlaceholder(doc, match[1]));
      } else if (src.includes('jotfor.ms') || src.includes('jotform.com')) {
        const handlerMatch = script.textContent?.match(/JotFormIFrame-(\d+)/);
        if (handlerMatch) discoveredIds.push(handlerMatch[1]);
        script.remove();
      } else {
        script.remove();
      }
    });

    doc.querySelectorAll('iframe[src*="jotform.com"]').forEach((iframe) => {
      const match = iframe.getAttribute('src')?.match(JOTFORM_FORM_URL_RE);
      if (match && isValidJotformFormId(match[1])) {
        discoveredIds.push(match[1]);
        iframe.replaceWith(createPlaceholder(doc, match[1]));
      } else {
        iframe.remove();
      }
    });

    const artifacts = [...doc.querySelectorAll('form.jotform-form, .form-section, .jotform-wrapper, ul.form-section')];
    artifacts.forEach((artifact) => {
      if (artifact.closest('.jotform-embed')) return;
      const formId =
        extractFormIdFromElement(artifact) ||
        (discoveredIds.length > 0 ? discoveredIds[0] : null);
      if (!formId) return;
      if (!discoveredIds.includes(formId)) discoveredIds.push(formId);
      const root = findJotformRoot(artifact, doc.body) || artifact;
      if (root.classList?.contains('jotform-embed')) return;
      root.replaceWith(createPlaceholder(doc, formId));
    });

    // Remove leftover raw Jotform field markup (common when full embed HTML was pasted).
    doc.querySelectorAll('.form-line, .form-input-wide, .form-buttons-wrapper, .form-pagebreak').forEach((el) => {
      if (el.closest('.jotform-embed')) return;
      el.remove();
    });

    doc.querySelectorAll('ul.form-section, form.jotform-form').forEach((el) => {
      if (el.closest('.jotform-embed')) return;
      if (discoveredIds.length > 0) {
        el.replaceWith(createPlaceholder(doc, discoveredIds[0]));
      } else {
        el.remove();
      }
    });

    doc.querySelectorAll('div.jotform-embed, div[class*="jotform-embed"]').forEach((el) => {
      const formId = el.getAttribute('data-form-id');
      if (!isValidJotformFormId(formId)) {
        el.remove();
        return;
      }
      el.className = 'jotform-embed';
      [...el.attributes].forEach((attr) => {
        if (attr.name !== 'class' && attr.name !== 'data-form-id') {
          el.removeAttribute(attr.name);
        }
      });
      el.innerHTML = '';
      el.removeAttribute('data-loaded');
    });

    const resolvedFormId = discoveredIds[0] || null;
    removeLegacyJotformFieldMarkup(doc, resolvedFormId);

    if (resolvedFormId && !doc.querySelector('.jotform-embed')) {
      doc.body.appendChild(createPlaceholder(doc, resolvedFormId));
    }

    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

export function createJotformEmbedHtml(formId) {
  return `<div class="jotform-embed" data-form-id="${formId}"></div>`;
}

export function normalizeJotformHtml(html) {
  return sanitizePostHtml(html);
}

/**
 * Clean up legacy/raw Jotform markup already injected into a live DOM container.
 */
export function normalizeJotformEmbedsInContainer(container) {
  if (!container || typeof DOMParser === 'undefined') return;

  const cleaned = sanitizePostHtml(container.innerHTML);
  if (cleaned !== container.innerHTML) {
    container.innerHTML = cleaned;
  }
}
